import { useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabaseWithAuth } from "@/utils/supabaseWrapper";
import { useAuth } from "./useAuth";
import { logger } from "@/utils/productionLogger";

// Types pour la nouvelle architecture
interface SubscriptionFeatures {
  brandingProEnabled: boolean;
  totalSeats: number;
  additionalSeats: number;
  creditBalance: number;
  monthlyRecurringCost: number;
  stripeCustomerId?: string;
  subscriptionStatus: 'active' | 'canceled' | 'past_due' | 'inactive';
}

interface OrganizationData {
  id: string;
  name: string;
  userRole: "owner" | "admin" | "member";
  usedSeats: number;
  features: SubscriptionFeatures;
}

export const useOrganizationFeatures = () => {
  const [organizationData, setOrganizationData] = useState<OrganizationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  logger.debug("useOrganizationFeatures hook initialized", { userEmail: user?.email, userId: user?.id });

  const fetchOrganizationData = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      setError(null);

      // Étape 1: Récupérer l'organisation ID depuis le profil de l'utilisateur avec protection auth
      const { data: profile } = await supabaseWithAuth.executeWithRetry(async () => {
        return supabaseWithAuth
          .from("profiles")
          .select("organization_id")
          .eq("id", user.id)
          .maybeSingle();
      }, 'organization_features.profile') as any;

      let orgId = profile?.organization_id;

      // Si pas d'org ID dans le profil, essayer via organization_members avec protection auth
      if (!orgId) {
        const { data: memberData } = await supabaseWithAuth.executeWithRetry(async () => {
          return supabaseWithAuth
            .from("organization_members")
            .select("organization_id, role, status")
            .eq("user_id", user.id)
            .eq("status", "active")
            .maybeSingle();
        }, 'organization_features.member_data') as any;

        orgId = memberData?.organization_id;
      }

      if (!orgId) {
        setOrganizationData(null);
        setLoading(false);
        return;
      }

      // Étape 2: Récupérer les données via une requête SQL directe avec protection auth
      const { data: orgFeaturesArray, error: orgError } = await supabaseWithAuth.executeWithRetry(async () => {
        return supabaseWithAuth
          .from('organization_features')
          .select('*')
          .eq('organization_id', orgId)
          .single();
      }, 'organization_features.features') as any;

      logger.debug("Organization features RPC result", { 
        hasData: !!orgFeaturesArray, 
        hasError: !!orgError,
        errorDetails: orgError,
        orgId: orgId,
        dataLength: Array.isArray(orgFeaturesArray) ? orgFeaturesArray.length : 0,
        actualData: orgFeaturesArray
      });

      // Vérifier si la requête a échoué OU si l'objet est vide OU si les données essentielles manquent
      const isEmptyObject = orgFeaturesArray && typeof orgFeaturesArray === 'object' && Object.keys(orgFeaturesArray).length === 0;
      const isMissingData = !orgFeaturesArray?.organization_id;
      
      if (orgError || !orgFeaturesArray || isEmptyObject || isMissingData) {
        // Fallback vers l'ancienne table si organization_features n'existe pas encore
        logger.error("FORCED FALLBACK - Organization features query failed", orgError || new Error('Unknown error'), { 
          orgId,
          isEmptyObject,
          isMissingData,
          dataReceived: orgFeaturesArray
        });
        
        const { data: legacyOrg, error: legacyError } = await supabaseWithAuth.executeWithRetry(async () => {
          return supabaseWithAuth
            .from("organizations")
            .select("id, name, branding_pro_enabled, total_seats, stripe_customer_id")
            .eq("id", orgId)
            .single();
        }, 'organization_features.legacy_org') as any;

        if (legacyError || !legacyOrg) {
          throw new Error("Organization not found");
        }

        // Utiliser les données legacy
        const features: SubscriptionFeatures = {
          brandingProEnabled: legacyOrg.branding_pro_enabled || false,
          totalSeats: legacyOrg.total_seats || 1,
          additionalSeats: Math.max((legacyOrg.total_seats || 1) - 1, 0),
          creditBalance: 0, // Géré par useCreditBalance
          monthlyRecurringCost: 0, // Non disponible dans legacy
          stripeCustomerId: legacyOrg.stripe_customer_id,
          subscriptionStatus: 'active'
        };

        // Récupérer le rôle utilisateur avec protection auth
        const { data: memberData } = await supabaseWithAuth.executeWithRetry(async () => {
          return supabaseWithAuth
            .from("organization_members")
            .select("role")
            .eq("user_id", user.id)
            .eq("organization_id", orgId)
            .eq("status", "active")
            .single();
        }, 'organization_features.member_role') as any;

        // Compter les sièges utilisés avec protection auth
        const { count: usedSeats } = await supabaseWithAuth.executeWithRetry(async () => {
          return supabaseWithAuth
            .from("organization_members")
            .select("id", { count: "exact", head: true })
            .eq("organization_id", orgId)
            .in("status", ["active", "invited"]);
        }, 'organization_features.used_seats') as any;

        setOrganizationData({
          id: legacyOrg.id,
          name: legacyOrg.name,
          userRole: (memberData?.role as "owner" | "admin" | "member") ?? "member",
          usedSeats: usedSeats || 0,
          features
        });

        logger.debug("Using legacy organization data");
        return;
      }

      // Utiliser les nouvelles données organization_features
      const orgFeatureData = orgFeaturesArray;
      
      const memberResult = await supabaseWithAuth.executeWithRetry(async () => {
        return supabaseWithAuth
          .from("organization_members")
          .select("role")
          .eq("user_id", user.id)
          .eq("organization_id", orgId)
          .eq("status", "active")
          .single();
      }, 'organization_features.new_member_role');
      
      const { data: memberData } = memberResult as any;

      // Compter les sièges utilisés avec protection auth
      const usedSeatsResult = await supabaseWithAuth.executeWithRetry(async () => {
        return supabaseWithAuth
          .from("organization_members")
          .select("id", { count: "exact", head: true })
          .eq("organization_id", orgId)
          .in("status", ["active", "invited"]);
      }, 'organization_features.new_used_seats');
      
      const usedSeats = (usedSeatsResult as any).count;

      const features: SubscriptionFeatures = {
        brandingProEnabled: orgFeatureData.branding_pro_enabled,
        totalSeats: orgFeatureData.total_seats,
        additionalSeats: orgFeatureData.additional_seats,
        creditBalance: 0, // Géré par useCreditBalance
        monthlyRecurringCost: parseFloat(orgFeatureData.monthly_total_cost || '0'),
        stripeCustomerId: orgFeatureData.stripe_customer_id,
        subscriptionStatus: 'active' // Calculé depuis stripe_data si nécessaire
      };

      // Récupérer le nom de l'organisation séparément avec protection auth
      const orgDataResult = await supabaseWithAuth.executeWithRetry(async () => {
        return supabaseWithAuth
          .from('organizations')
          .select('name')
          .eq('id', orgId)
          .single();
      }, 'organization_features.org_name');
      
      const orgData = (orgDataResult as any).data;

      const finalOrgData = {
        id: orgFeatureData.organization_id,
        name: orgData?.name || 'Organisation',
        userRole: (memberData?.role as "owner" | "admin" | "member") ?? "member",
        usedSeats: usedSeats || 0,
        features
      };
      
      setOrganizationData(finalOrgData);

    } catch (_error) {
      logger.error("Error fetching organization data", _error as Error);
      setError((_error as Error).message);
      setOrganizationData(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id]); // ✅ Dépendre uniquement de user.id

  // Fonction de réconciliation manuelle
  const reconcileFeatures = useCallback(async () => {
    if (!organizationData) {
      logger.debug("No organization data, cannot reconcile");
      return;
    }

    try {
      logger.debug("Starting manual reconciliation", { orgId: organizationData.id });
      
      const { data, error } = await supabaseWithAuth.client.functions.invoke('reconcile-organization-features', {
        body: { 
          organization_id: organizationData.id,
          force_sync: true 
        }
      });
      
      if (error) {
        throw error;
      }

      logger.debug("Reconciliation completed", data);
      
      // Recharger les données après réconciliation
      await fetchOrganizationData();
      
      return data;
      
    } catch (_error) {
      logger.error('Error reconciling features:', _error as Error);
      setError('Erreur lors de la synchronisation des fonctionnalités');
      throw _error;
    }
  }, [organizationData, fetchOrganizationData]);

  useEffect(() => {
    // Éviter les appels multiples en vérifiant si on a déjà des données
    if (!user) return;
    
    fetchOrganizationData();

    // Écouter les changements en temps réel sur la nouvelle table
    const featuresChannel = supabaseWithAuth.client
        .channel('organization-features-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'organization_features'
          },
          (payload) => {
            logger.debug('Organization features changed, refreshing data', payload);
            fetchOrganizationData();
          }
        )
        .subscribe();

      // Écouter les changements de rôle sur organization_members
      const membersChannel = supabaseWithAuth.client
        .channel('organization-members-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'organization_members',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            logger.debug('User role changed, refreshing organization data', payload);
            // Invalider immédiatement les queries dépendantes du rôle
            queryClient.invalidateQueries({ queryKey: ['campaigns'] });
            queryClient.invalidateQueries({ queryKey: ['reports'] });
            // Puis recharger les données d'organisation
            fetchOrganizationData();
          }
        )
        .subscribe();

      return () => {
        supabaseWithAuth.client.removeChannel(featuresChannel);
        supabaseWithAuth.client.removeChannel(membersChannel);
      };
  }, [user?.id]); // ⚠️ Dépendre uniquement de user.id, pas de fetchOrganizationData

  // Permissions calculées
  const canManageMembers = organizationData?.userRole === "owner" || organizationData?.userRole === "admin";
  const canManageBranding = canManageMembers && organizationData?.features.brandingProEnabled;
  const canAddMoreSeats = organizationData ? organizationData.usedSeats < organizationData.features.totalSeats : false;
  const hasActiveSubscription = organizationData?.features.subscriptionStatus === 'active';
  
  logger.debug("Permissions calculated", { 
    canManageMembers, 
    canManageBranding, 
    canAddMoreSeats,
    hasActiveSubscription,
    hasOrgData: !!organizationData,
    usedSeats: organizationData?.usedSeats,
    totalSeats: organizationData?.features.totalSeats,
    calculation: `${organizationData?.usedSeats} < ${organizationData?.features.totalSeats} = ${canAddMoreSeats}`
  });

  return {
    organizationData,
    loading,
    error,
    canManageMembers,
    canManageBranding,
    canAddMoreSeats,
    hasActiveSubscription,
    refreshOrganizationData: fetchOrganizationData,
    reconcileFeatures, // Nouvelle fonction pour forcer la sync
  };
};